"use client";

import { type TotpChallenge, useTotpChallenge } from "./useTotpChallenge";
import { useSupabase } from "@/app/supabase-provider";
import { trackError } from "@/components/error/trackError";
import { fireAndForget } from "@/lib/vercelUtils";
import { assertNever } from "@/packages/ts-utils";
import { getMfaSessionState } from "@/utils/auth/mfa";
import { useEffect, useState } from "react";

/** Checks recovery-session MFA before exposing the password form. */
type PasswordRecoveryMfaStepUp =
  | { status: "checking" }
  | { status: "failed"; error: string }
  | { status: "passwordForm" }
  | ({ status: "challenge" } & TotpChallenge);

type InternalPasswordRecoveryMfaStepUpState =
  | { status: "checking" }
  | { status: "failed"; error: string }
  | { status: "passwordForm" }
  | { status: "challenge" };

export const usePasswordRecoveryMfaStepUp = (): PasswordRecoveryMfaStepUp => {
  const { supabase } = useSupabase();
  const [state, setState] = useState<InternalPasswordRecoveryMfaStepUpState>({
    status: "checking",
  });
  const challenge = useTotpChallenge({
    supabase,
    purpose: { kind: "challenge", context: "password_recovery" },
    onVerified: () => setState({ status: "passwordForm" }),
  });

  useEffect(() => {
    let isActive = true;
    const resolve = async () => {
      try {
        const { isStepUpRequired } = await getMfaSessionState({ supabase });
        if (!isActive) return;
        setState(
          isStepUpRequired
            ? { status: "challenge" }
            : { status: "passwordForm" }
        );
      } catch (error) {
        if (!isActive) return;
        setState({
          status: "failed",
          error:
            "Could not check authentication. Please refresh and try again.",
        });
        fireAndForget(
          trackError({
            error:
              error instanceof Error
                ? error
                : new Error("MFA session lookup failed"),
            componentStack: null,
            eventId: null,
            errorPlace: "mfa_session_lookup",
          })
        );
      }
    };
    fireAndForget(resolve());
    return () => {
      isActive = false;
    };
  }, [supabase]);

  switch (state.status) {
    case "checking":
    case "failed":
    case "passwordForm":
      return state;
    case "challenge":
      return {
        status: "challenge",
        ...challenge,
      };
    default:
      return assertNever(state);
  }
};
